import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Building2, Factory, Gauge, Power, Settings, Wind, Grid3x3, Wrench, ArrowRight, CheckCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

export const Services = () => {
  // Smooth scroll to section on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const detailedServices = [
    {
      id: 'epc',
      title: 'Electrical & Instrumentation EPC Contracting',
      description: 'Complete turnkey solutions covering design, engineering, procurement, supply, erection, testing, and commissioning for electrical and instrumentation projects.',
      features: ['Turnkey Project Execution', 'Design & Engineering', 'Quality Assurance', 'Post-Commissioning Support'],
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/orlbw6t9_Station%20with%20SLD.png'
    },
    {
      id: 'renewable',
      title: 'Renewable Energy Solutions',
      description: 'Comprehensive renewable energy services including erection, testing, and commissioning of solar power systems and associated electrical infrastructure.',
      features: ['IDT, Inverters, ICOG, ACDB, UPS', 'HT/LT Installations & Integration', 'AC & DC Cable Systems', 'AC/DC Distribution & Auxiliaries', 'Power Substation System (PSS) up to 220 kV'],
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/vrztcxjm_AdobeStock_131872217.jpeg'
    },
    {
      id: 'substation',
      title: 'Substation & Switchyard Works',
      description: 'Complete substation projects with execution up to 220 kV.',
      features: ['Substation Design & Engineering', 'Equipment Installation', 'Protection Systems', 'Testing & Commissioning'],
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/xxsadtoc_a04216058599091312f0df88b58f54bb.jpg'
    },
    {
      id: 'electrical-instrumentation',
      title: 'Electrical & Instrumentation',
      description: 'Precision installation of control systems, panels, and instrumentation for process industries.',
      features: ['Control Panel Installation (MCC/PCC)', 'Cable Trays & Termination', 'Field Instruments & Junction Boxes', 'Commissioning & Testing Support'],
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/g2jng9zv_E%26I-2.jpeg'
    },
    {
      id: 'transmission',
      title: 'Transmission Lines up to 400 kV',
      description: 'Complete turnkey transmission line execution including supply, erection, stringing, testing, and commissioning.',
      features: ['Supply of Transmission Line Materials', 'Tower Erection Works', 'Conductor & OPGW Stringing', 'Testing & Commissioning', 'Grid Infrastructure Integration'],
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&h=600&fit=crop'
    },
    {
      id: 'cogen-sugar',
      title: 'Cogen & Sugar',
      description: 'Specialized electrical and instrumentation works for sugar plants and co-generation facilities.',
      features: ['Complete E&I Works', 'Erection & Installation', 'Cable Laying & Termination', 'Transformers, Panels (MCC/PCC), Switchgear', 'Testing & Commissioning Support'],
      image: 'https://customer-assets.emergentagent.com/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/946j1m2p_manjara-shetkari-sahakari-sakhar-karkhana-ltd-latur-ho-latur-sugar-manufacturers-31i1bqx.avif'
    },
  ];

  const epcExpertise = [
    {
      icon: <Wind className="w-8 h-8" />,
      title: 'Renewable Power Evacuation',
      industries: ['Solar Plants', 'Wind Farms', 'Hybrid Projects']
    },
    {
      icon: <Grid3x3 className="w-8 h-8" />,
      title: 'Switchyards up to 220 kV',
      industries: ['Power Generation', 'Substations', 'Grid Connections']
    },
    {
      icon: <Power className="w-8 h-8" />,
      title: 'Transmission Lines up to 400 kV',
      industries: ['Overhead Lines', 'Underground Cables', 'Grid Infrastructure']
    },
    {
      icon: <Factory className="w-8 h-8" />,
      title: 'Industrial Projects',
      industries: ['Sugar Co-Gen Plants', 'Distilleries', 'Manufacturing Units']
    },
  ];

  const erectionServices = [
    'Power Plants & Co-Generation Facilities',
    'Sugar & Cement Industries',
    'Textile & Spinning Units',
    'Distilleries & Dairies',
    'Steel & Heavy Process Plants',
    'Manufacturing Industries',
  ];

  const processSteps = [
    { number: '01', title: 'Design & Engineering', description: 'Comprehensive project planning and engineering solutions' },
    { number: '02', title: 'Procurement & Supply', description: 'Quality equipment sourcing and material management' },
    { number: '03', title: 'Erection & Installation', description: 'Expert installation by skilled workforce' },
    { number: '04', title: 'Testing & Commissioning', description: 'Rigorous testing and successful project handover' }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      <SEO 
        title="Our Services | Calspark Electrical & Engineers"
        description="Calspark offers end-to-end EPC services including substation erection, HV/EHV transmission lines, renewable energy systems, cogeneration projects, and electrical commissioning across Pan India."
        keywords="substation erection, EHV transmission line, cogeneration EPC, electrical commissioning, instrumentation services India, EPC contracts"
        ogUrl="/services"
      />
      {/* Hero Section with Background Image */}
      <section className="relative bg-gradient-to-br from-slate-800 to-slate-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1509390484649-e8f3d1bb0b6d?w=1920&h=600&fit=crop" 
            alt="Services background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Comprehensive EPC solutions for electrical and instrumentation projects across diverse industry sectors
          </p>
        </div>
      </section>

      {/* Detailed Services Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 space-y-24">
          {detailedServices.map((service, index) => (
            <div 
              key={service.id} 
              id={service.id} 
              className="scroll-mt-32"
            >
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}>
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mt-0.5">
                          <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                        </div>
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
                  >
                    Get a Quote for This Service <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EPC Project Expertise */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">EPC Project Expertise</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Specialized execution across multiple industry segments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {epcExpertise.map((expertise, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="text-teal-600 mb-4">{expertise.icon}</div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">{expertise.title}</h3>
                <ul className="space-y-2">
                  {expertise.industries.map((industry, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-teal-600" />
                      {industry}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Our Process</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A proven 4-step approach for successful project delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white h-full">
                  <div className="text-5xl font-bold text-teal-400 mb-4">{step.number}</div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-slate-300 text-sm">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-teal-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Erection, Testing & Commissioning */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Erection, Testing & Commissioning
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our comprehensive execution capability extends to complete erection, testing, and commissioning works across various industrial sectors. We ensure seamless project delivery with rigorous quality checks at every stage.
              </p>

              <div className="space-y-4">
                {erectionServices.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 bg-white rounded-lg hover:bg-teal-50 transition-colors shadow-sm"
                  >
                    <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1509390288171-ce2088f7d08e?w=800&h=600&fit=crop"
                alt="Industrial Plant"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                <div className="text-3xl font-bold text-teal-600">220 kV</div>
                <div className="text-slate-600 font-medium">Substation Capacity</div>
                <div className="text-sm text-slate-500 mt-1">400 kV Transmission</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Expert EPC Solutions?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Our team is ready to discuss your project requirements and deliver tailored solutions
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-slate-50 transition-all duration-300 shadow-lg"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};
